import { invariant } from '@mimir/common/util/invariant';
import { exceptionSummary, makeError } from '@mimir/common/util/exception';
import type { CognitoUserSession, IAuthenticationCallback, ICognitoStorage } from 'amazon-cognito-identity-js';
import { AuthenticationDetails, CognitoRefreshToken, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import type { ServiceCache } from '@mimir/common/client/serviceCache';
import { browserAuthorizeUrl, fetchTokensFromCodeGrant, fetchWellKnownOidcConfig } from '@mimir/common/util/openIdConnect';
import { getOpenIdConnectProviderUrl } from '@mimir/common/util/cognitoRequest';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { OBJ_PROXY_PATH_QUERY_PARAM } from '@mimir/common/mos/mos-interfaces';
import { isAxiosError } from '@mimir/common/util/httpError';
import { exceptionCode } from '@mimir/common/util/errnoException';
import { Piggyback } from '@mimir/common/async/taskTracking';
import type { AuthHeaderSource } from '@mimir/common/client/mimirCalls';
import { MIMIR_JWT_HTTP_HEADER } from '@mimir/common/util/constants';
import { getTenantIdFromJwt } from './jwtHelpers';

/**
 * Login input which PersistentUserSession instances can handle in order to
 * mutate remote Cognito user pool state, local session tokens, and login state.
 *
 * This input typically originate from a UI that presents an interactive login
 * workflow to the user.
 */
export type LoginInput = AuthenticateWithPassword
  | RedirectToIdentityProvider
  | AuthenticateWithQueryParameters
  | NewPassword
  | ForgotPassword
  | ConfirmForgotPassword
  | TOTPVerification;

export interface AuthenticateWithPassword {
  type: 'AuthenticateWithPassword';
  username: string;
  password: string;
}

export interface TOTPVerification {
  type: 'TOTPVerification';
  totpCode: string;
}

export interface RedirectToIdentityProvider {
  type: 'RedirectToIdentityProvider';
  identityProvider: string;
}

export interface AuthenticateWithQueryParameters {
  type: 'AuthenticateWithQueryParameters';
  query: Record<string, string>;
}

export interface NewPassword {
  type: 'NewPassword';
  newPassword: string,
  requiredAttributeData: Record<string, string>
}

export interface ForgotPassword {
  type: 'ForgotPassword';
  username: string;
}

export interface ConfirmForgotPassword {
  type: 'ConfirmForgotPassword';
  username: string;
  confirmationCode: string;
  newPassword: string;
}

export interface ChangePasswordInput {
  oldPassword: string;
	newPassword: string;
}

/**
 * An authentication phase that is the result of applying a login input to a
 * user session.
 *
 * Applying login input can either result in a follow up authentication
 * challenge, the authentication completing or neither.
 *
 * As an example, logging in using username and temporary password of a newly
 * created account would result in an authentication challenge requesting that a
 * new password is provided.
 */
type AuthenticationPhase = NewPasswordRequired | AuthenticationCompleted | TotpRequired | null;

export interface NewPasswordRequired {
  type: 'NewPasswordRequired';
  userAttributes: Record<string, string>,
  requiredAttributes: string[]
}

export interface TotpRequired{
  type: 'TotpRequired',
  challengeName: string,
  challengeParameters: Record<string, string>,
}

interface AuthenticationCompleted {
  type: 'AuthenticationCompleted'
}

export interface UrlPathState {
  pathname: string;
  search: string;
  hash: string;
  target: 'self' | 'opener';
}

export const NETWORK_ERROR = 'Network error';

/**
 * Here is the state that the user session expose to UIs to allow the UIs to
 * react to.
 */
export interface LoginState {
  /**
   * True when an login operation has been started and is still pending.
   */
  busy: boolean;

  /**
   * Description of last login attempt failure.
   *
   * This is reset to null every time a new login attempt is started.
   */
  error: string | null;

  /**
   * The phase of the authentication process we're currently at.
   */
  phase: AuthenticationPhase;

  /**
   * The refresh token both indicates whether the user is logged in, and also
   * provides the refresh token to use when showing Kelda configuration
   * instructions.
   *
   * If this value remains null after a call to initialize(), then we do not
   * have sufficient state for performing API calls on the behalf the user, and
   * the UI should show a login form.
   */
  refreshToken: string | null;

  /**
   * Access token provided by Cognito.
   */
  accessToken: string | null;

  /**
   * Claims from the JWT ID Token. Is empty until initialized.
   */
  claims: Partial<IdTokenClaims>;

  needsVerification: boolean | null;
}

/**
 * Describes the subset of claims exposed in the login state that the UI
 * application makes use of. This type definition does not cover all the claims
 * available. We may expand this type definition in the future.
 */
export interface IdTokenClaims {
  email: string;
  email_verified: boolean;
  sub: string;
}

/**
 * White listed params in MOS (/browse)
 */
export const mosPersistedParams = <const>['mosId', 'origin', 'organization', 'mos', 'ncsItem', 'iopp', OBJ_PROXY_PATH_QUERY_PARAM];

/**
 * White listed params in Vimond (/export)
 */
const exportPersistedParams = <const>['organization'];

/**
 * White listed params in ROOT (/)
 */
const rootPersistedParams = <const>['organization'];

/**
 * Routes that need to persist some params after logout.
 */
enum RoutesWithWhiteListedParams {
  MOS = '/browse',
  EXPORT = '/export',
  ROOT = '/'
}

/**
 * Parameters that need to persist in URL after logout.
 * Parameters are subject to route.
 * Like /browse route (MOS plugin) requires mosId and origin param.
 */
const whiteListedParams: Record<RoutesWithWhiteListedParams, readonly string[]> = {
  [RoutesWithWhiteListedParams.MOS]: mosPersistedParams,
  [RoutesWithWhiteListedParams.EXPORT]: exportPersistedParams,
  [RoutesWithWhiteListedParams.ROOT]: rootPersistedParams
};

const refreshSessionPiggyback = new Piggyback<CognitoUserSession>();

/**
 * Implements a sticky user login session, meaning that it remembers which user
 * that was logged in, an will try to continue refreshing tokens for this user,
 * even if other browser tabs log in as a different user.
 *
 * Allows UIs to provide a LoginState object instrumented for reactivity, to
 * allow the UI to directly binding the login state to the UI components.
 */
export class PersistentUserSession {
  user: CognitoUser | null = null;
  loginState: LoginState;
  popUp: Window | null = null;
  refreshedOnce: boolean = false;

  constructor(
    private serviceCache: ServiceCache,
    private storage: ICognitoStorage,
    loginState?: LoginState
  ) {
    this.loginState = loginState ?? makeEmptyLoginState();
    this.loginState.busy = false;
  }

  /**
   * Initialize LoginState.
   *
   * This function is idempotent, and can safely be called multiple times.
   */
  async initialize(windowLocation: Location): Promise<boolean> {
    const user = await this.tryGetUser();
    if (user == null) {
      return false;
    }

    const searchParams = new URL(windowLocation.href).searchParams;
    const email = searchParams.get('email') ?? '';
    const tenantId = searchParams.get('organizationId') ?? '';
    if (email) {
      const emailDecoded = decodeURIComponent(email);
      const emailFromJwt = (await this.getJwtPayload()).email;
      if (emailDecoded !== emailFromJwt) {
        await this.signOut();
      }
    } else if (tenantId) {
      const tenantIdDecoded = decodeURIComponent(tenantId);
      const tenantIdFromJwt = getTenantIdFromJwt(await this.getJwtPayload());
      if (tenantIdDecoded !== tenantIdFromJwt) {
        await this.signOut();
      }
    }

    try {
      await this.acquireFreshSessionForUser(user);
    } catch (ex) {
      return false;
    }
    return true;
  }

  async handleLoginInput(input: LoginInput): Promise<AuthenticationPhase> {
    switch (input.type) {
      case 'AuthenticateWithPassword':
        return this.authenticateWithPassword(input);
      case 'RedirectToIdentityProvider':
        return this.redirectToIdentityProvider(input);
      case 'AuthenticateWithQueryParameters':
        return this.authenticateWithQueryParameters(input);
      case 'NewPassword':
        return this.newPassword(input);
      case 'ForgotPassword':
        return this.forgotPassword(input);
      case 'ConfirmForgotPassword':
        return this.confirmForgotPassword(input);
      case 'TOTPVerification':
        return this.handleTOTPVerification(input);
      default:
        throw makeError('Unexpected login input', 'LoginError', { info: { input } });
    }
  }

  private authenticateWithPassword(input: Omit<AuthenticateWithPassword, 'type'>): Promise<AuthenticationPhase> {
    return this.monitorAction(async () => {
      const Pool = await this.getUserPool();
      const user = new CognitoUser({
        Username: input.username,
        Pool,
        Storage: this.storage,
      });
      this.user = user;
      return new Promise((resolve, reject) => {
        user.authenticateUser(
          new AuthenticationDetails({
            Username: input.username,
            Password: input.password,
          }),
          makeAuthenticationCallback(resolve, reject)
        );
      });
    });
  }

  private handleTOTPVerification(input: Omit<TOTPVerification, 'type'>): Promise<AuthenticationPhase> {
    return this.monitorAction(async () => {
      const user = this.user;
      invariant(user != null, 'Expected current user');
      return new Promise((resolve, reject) => {
        user.sendMFACode(
          input.totpCode,
          {
            onSuccess: () => resolve({ type: 'AuthenticationCompleted' }),
            onFailure: reject,
          },
          'SOFTWARE_TOKEN_MFA',
        );
      });
    });
  }

  async redirectToIdentityProvider(input: Omit<RedirectToIdentityProvider, 'type'>): Promise<AuthenticationPhase> {
    this.closePopup();

    const isInAdobeContext = (window as any).__adobe_cep__  !== undefined;
    const isTopLevel = window.self == window.top;
    if (!isTopLevel && !isInAdobeContext) {
      const popUpUrl = await getBrowserAuthorizeUrlForIdentityProvider(this.serviceCache, window.location.href, input.identityProvider, 'opener');
      this.popUp = window.open(popUpUrl);
      if (this.popUp != null) {
        return null;
      }
    }

    const redirectUrl = await getBrowserAuthorizeUrlForIdentityProvider(this.serviceCache, window.location.href, input.identityProvider, 'self');
    window.location.href = redirectUrl;
    return null;
  }

  closePopup() {
    if (this.popUp) {
      this.popUp.close();
      this.popUp = null;
    }
  }

  authenticateWithQueryParameters(input: Omit<AuthenticateWithQueryParameters, 'type'>): Promise<AuthenticationPhase> {
    this.closePopup();
    if (input.query.error || input.query.error_description) {
      this.loginState.error = (input.query.error ?? 'Login error') + ': ' + input.query.error_description;
      return Promise.resolve(null);
    }

    return this.monitorAction(async () => {
      if (typeof input.query.code === 'string') {

        let state: any;
        try {
          state = JSON.parse(input.query.state);
        } catch (_) {
          // pass
        }

        if (state && typeof state === 'object') {
          const stateObj: Partial<UrlPathState> = state;
          if (stateObj.target == 'opener' && window.opener) {
            const msg: AuthenticateWithQueryParameters = {
              type: 'AuthenticateWithQueryParameters',
              ...input
            };

            window.opener.postMessage(msg, window.location.origin);
            /*
              The work of this pop-up browser window is now completed. The
              opener will confirm reception of the authentication parameters by
              closing us.
            */
            return {
              type: 'AuthenticationCompleted'
            };
          }
        }

        const serviceDocument = await this.serviceCache.getServiceDocument();
        const oidcConfig = await fetchWellKnownOidcConfig(axios, getOpenIdConnectProviderUrl(serviceDocument.clientConfig.authRegion, serviceDocument.clientConfig.userPoolId, serviceDocument.clientConfig.cognitoEndpoint));
        const tokens = await fetchTokensFromCodeGrant(axios, input.query.code, { client_id: serviceDocument.clientConfig.appClientId, redirect_uri: makeRedirectBaseUrl(window.location.href) }, oidcConfig.token_endpoint);
        const Pool = await this.getUserPool();
        const user = new CognitoUser({
          /*
            TODO: Consider whether there is any value in providing a more specific
            user name here.
          */
          Username: 'code-authenticated-user',
          Pool,
          Storage: this.storage
        });
        await refreshSession(user, new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }));
        return {
          type: 'AuthenticationCompleted'
        };
      }

      return null;
    });
  }

  private newPassword(input: Omit<NewPassword, 'type'>): Promise<AuthenticationPhase> {
    return this.monitorAction(async () => {
      const user = this.user;
      invariant(user != null, 'Expected current user');
      return new Promise((resolve, reject) => {
        user.completeNewPasswordChallenge(
          input.newPassword,
          input.requiredAttributeData,
          makeAuthenticationCallback(resolve, reject)
        );
      });
    });
  }

  private forgotPassword(input: Omit<ForgotPassword, 'type'>): Promise<AuthenticationPhase> {
    return this.monitorAction(async () => {
      const Pool = await this.getUserPool();
      const user = new CognitoUser({
        Username: input.username,
        Pool,
        Storage: this.storage,
      });
      await new Promise((resolve, reject) => {
        user.forgotPassword({
          onSuccess: resolve,
          onFailure: reject,
          inputVerificationCode: resolve
        });
      });
      return null;
    });
  }

  private confirmForgotPassword(input: Omit<ConfirmForgotPassword, 'type'>): Promise<AuthenticationPhase> {
    return this.monitorAction(async () => {
      const Pool = await this.getUserPool();
      const user = new CognitoUser({
        Username: input.username,
        Pool,
        Storage: this.storage,
      });
      await new Promise<void>((resolve, reject) => {
        user.confirmPassword(input.confirmationCode, input.newPassword, {
          onSuccess: () => resolve(),
          onFailure: reject,
        });
      });
      return null;
    });
  }

  async changePassword(input: ChangePasswordInput): Promise<void> {
    const user = await this.tryGetUser();
    invariant(user != null, 'Expected current user');
    return new Promise((resolve, reject) => {
      user.getSession((err: Error)=> {
        if (err) {
          reject(err);
        } else {
          user.changePassword(input.oldPassword, input.newPassword, (error) => {
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          });
        }
      });
    });
  }

  async signOut() {
    const user = await this.tryGetUser();
    if (user == null) {
      return;
    }

    user.signOut();
    this.loginState.claims = {};
    this.loginState.error = null;
    this.loginState.needsVerification = false;
    this.loginState.phase = null;
    this.loginState.refreshToken = null;
    this.loginState.accessToken = null;
  }

  async redirectToSignOut(orgToRedirectTo?: string) {
    await this.signOut();
    const currentUrl = window.location.href;

    const logoutUrl = new URL(currentUrl);

    /*
      Remove any previous organization parameter before considering whether to
      add a new one.
    */
    logoutUrl.searchParams.delete('organization');
    if (orgToRedirectTo) {
      logoutUrl.searchParams.append('organization', orgToRedirectTo);
    }

    const state = urlToPathState(logoutUrl, 'self');
    this.storage.setItem('after-logout-state', JSON.stringify(state));
    window.location.href = await getBrowserLogoutUrl(this.serviceCache, currentUrl);
  }

  getAfterLogoutRedirectPath() {
    const stateStr = this.storage.getItem('after-logout-state');
    if (!stateStr) {
      return '/';
    }
    const state: UrlPathState = JSON.parse(stateStr);
    const routeTypes = Object.keys(whiteListedParams) as RoutesWithWhiteListedParams[];
    for (const routeType of routeTypes) {
      if (state.pathname.startsWith(routeType, 0)) {
        return this.getUrlWithWhiteListedParams(routeType, state.search);
      }
    }
    return '/';
  }

  getUrlWithWhiteListedParams(routeType: RoutesWithWhiteListedParams, searchString: string): string {
    const whiteParams = whiteListedParams[routeType];
    if (!whiteParams.length) {
      return routeType;
    }
    const oldSearchParams = new URLSearchParams(searchString);
    const newSearchParams = new URLSearchParams({});
    whiteParams.forEach(param => {
      const value = oldSearchParams.get(param);
      if (value) {
        newSearchParams.append(param, value);
      }
    });
    return routeType + '?' + newSearchParams.toString();
  }


  async getFreshJwt() {
    const idToken = await this.getFreshIdToken();
    return idToken.getJwtToken();
  }

  async getJwtPayload() {
    const idToken = await this.getFreshIdToken();
    return idToken.decodePayload();
  }

  private async monitorAction(action: () => Promise<AuthenticationPhase>) {
    try {
      this.loginState.busy = true;
      this.loginState.error = null;
      const nextPhase = await action();
      if (nextPhase?.type == 'AuthenticationCompleted') {
        await this.acquireFreshSession();
      }

      this.loginState.phase = nextPhase;
      return nextPhase;
    } catch (ex) {
      this.loginState.error = loginExceptionSummary(ex);
      throw ex;
    } finally {
      this.loginState.busy = false;
    }
  }

  private async acquireFreshSession() {
    try {
      const user = await this.tryGetUser();
      if (user == null) {
        throw new Error('No user is logged in');
      }
      return await this.acquireFreshSessionForUser(user);
    } catch (ex) {
      this.loginState.refreshToken = null;
      this.loginState.accessToken = null;
      this.loginState.error = exceptionCode(ex) === 'NetworkError' ? NETWORK_ERROR : exceptionSummary(ex);
      throw ex;
    }
  }

  private async acquireFreshSessionForUser(user: CognitoUser) {
    const session = await this.getFreshSession(user);
    const claims : Partial<IdTokenClaims> = session.getIdToken().payload;
    this.loginState.refreshToken = session.getRefreshToken().getToken();
    this.loginState.accessToken = session.getAccessToken().getJwtToken();
    this.loginState.claims = claims;
    this.loginState.needsVerification = claims.email_verified != true;
    return session;
  }

  private async getFreshSession(user: CognitoUser) {
    let session = await getSession(user);
    const clockDrift = (session as { clockDrift?: number }).clockDrift ?? 0;
    const now = Date.now();
    const tenMinutesFromNow = now / 1000 + 600 - clockDrift;

    const expiration = session.getIdToken().getExpiration();
    let shouldRefresh = tenMinutesFromNow > expiration;
    if ((!shouldRefresh) && (!this.refreshedOnce) && (Math.abs(clockDrift) > 60 * 5)) {
      /*
        Prevent getting into a state in which bad stored clock drift causes an
        expired token not to get refreshed even though it is actually expired.
      */
      console.warn('Refreshing token once to be on the safe side, since clock drift is', clockDrift);
      shouldRefresh = true;
    }

    if (shouldRefresh) {
      // Token will expire within 10 minutes. Need to refresh it now.
      session = await refreshSession(user, session.getRefreshToken());
      this.refreshedOnce = true;
    }
    return session;
  }


  private async getFreshIdToken() {
    const session = await refreshSessionPiggyback.runOrPiggyback(() => this.acquireFreshSession());
    return session.getIdToken();
  }

  private async getUserPool() {
    try {
      const serviceDocument = await this.serviceCache.getServiceDocument();
      const pool = new CognitoUserPool({
        endpoint: serviceDocument.clientConfig.cognitoEndpoint,
        UserPoolId: serviceDocument.clientConfig.userPoolId,
        ClientId: serviceDocument.clientConfig.appClientId,
        Storage: this.storage,
      });
      return pool;
    } catch (ex) {
      this.loginState.error = exceptionSummary(ex);
      throw ex;
    }
  }

  private async tryGetUser(): Promise<CognitoUser | null> {
    if (this.user == null) {
      const pool = await this.getUserPool();
      this.user = pool.getCurrentUser();
    }

    return this.user;
  }
}

export function makeEmptyLoginState(): LoginState {
  return {
    busy: false,
    error: null,
    phase: null,
    refreshToken: null,
    accessToken: null,
    claims: {},
    needsVerification: null
  };
}

function makeAuthenticationCallback(resolve: (value: AuthenticationPhase) => void, reject: (reason?: any) => void): IAuthenticationCallback {
  return {
    onSuccess() {
      resolve({
        type: 'AuthenticationCompleted'
      });
    },
    onFailure(err: any) {
      reject(err);
    },
    newPasswordRequired(userAttributes: any, requiredAttributes: any) {
      const challenge: NewPasswordRequired = {
        type: 'NewPasswordRequired',
        userAttributes,
        requiredAttributes
      };
      resolve(challenge);
    },
    totpRequired(challengeName: any, challengeParameters: any) {
      const challenge: TotpRequired = {
        type: 'TotpRequired',
        challengeName,
        challengeParameters,
      };
      resolve(challenge);
    },
  };
}

function getSession(user: CognitoUser): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.getSession(makeSessionCallback(resolve, reject));
  });
}

function refreshSession(user: CognitoUser, refreshToken: CognitoRefreshToken): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    user.refreshSession(refreshToken, makeSessionCallback(resolve, reject));
  });
}

function makeSessionCallback(resolve: (session: CognitoUserSession) => void, reject: (err: any) => void) {
  return (error: Error | null, session: CognitoUserSession | null) => {
    if (error || !session) {
      reject(error);
      return;
    }

    resolve(session);
  };
}

function loginExceptionSummary(ex: unknown) {
  if (isAxiosError(ex)) {
    const response = ex.response as Partial<AxiosResponse> | undefined;
    if (response?.status == 400 && response?.data?.error == 'invalid_grant') {
      return 'Invalid, expired, or already used sign-in code';
    }
  }

  if (exceptionCode(ex) === 'UserNotFoundException') {
    return 'The email you entered doesn\'t exist in our system.';
  }

  const message = exceptionSummary(ex);

  if (message === 'Invalid session for the user, session is expired.') {
    return 'Error occurred while processing your request. Please reload the page and restart the login process.';
  }
  return message;
}

/*
  TODO: Improve security by implementing Proof Key of Code Exchange (PKCE), as
  defined by RFC 7636.
*/
export async function getBrowserAuthorizeUrlForIdentityProvider(serviceCache: ServiceCache, currentLocationUrl: string, identity_provider: string, target: 'self' | 'opener') {
  const serviceDocument = await serviceCache.getServiceDocument();
  const oidcConfig = await fetchWellKnownOidcConfig(axios, getOpenIdConnectProviderUrl(serviceDocument.clientConfig.authRegion, serviceDocument.clientConfig.userPoolId, serviceDocument.clientConfig.cognitoEndpoint));

  const url = new URL(currentLocationUrl);
  const state = urlToPathState(url, target);
  stripAllPath(url);

  const params: Record<string, string> = {
    state: JSON.stringify(state)
  };

  if (identity_provider) {
    params.identity_provider = identity_provider;
  }

  return browserAuthorizeUrl(
    oidcConfig.authorization_endpoint,
    {
      client_id: serviceDocument.clientConfig.appClientId,
      redirect_uri: url.toString()
    },
    'openid profile email',
    params
  );
}

export async function getBrowserLogoutUrl(serviceCache: ServiceCache, currentLocationUrl: string) {
  /*
    The OpenID Connect service document does not contain the logout endpoint,
    but we still need the service document to discover the domain name to use,
    since the client registration information we have at hand does not provide
    this information.

  */
  const serviceDocument = await serviceCache.getServiceDocument();
  const oidcConfig = await fetchWellKnownOidcConfig(axios, getOpenIdConnectProviderUrl(serviceDocument.clientConfig.authRegion, serviceDocument.clientConfig.userPoolId, serviceDocument.clientConfig.cognitoEndpoint));

  const logoutUrl = new URL(currentLocationUrl);
  // The logout URL does not accept any query parameters.
  stripAllPath(logoutUrl);
  logoutUrl.pathname = '/after-logout';

  const url = new URL(oidcConfig.authorization_endpoint);
  url.pathname = 'logout';
  const query = new URLSearchParams({
    client_id: serviceDocument.clientConfig.appClientId,
    logout_uri: logoutUrl.toString()
  });
  url.search = query.toString();
  return url.toString();
}

function urlToPathState(url: URL, target: 'self' | 'opener'): UrlPathState {
  return {
    pathname: url.pathname,
    hash: url.hash,
    search: url.search,
    target
  };
}

function stripAllPath(url: URL) {
  url.pathname = '';
  url.hash = '';
  url.search = '';
}

function makeRedirectBaseUrl(currentLocationUrl: string) {
  const url = new URL(currentLocationUrl);
  stripAllPath(url);
  return url.toString();
}

/**
 * Returns value of whitelisted param.
 * White listed params are persisted after logout.
 */
export const getWhiteListedParamValue = (
  param: typeof mosPersistedParams[number] | typeof exportPersistedParams[number] | typeof rootPersistedParams[number]
) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(param);
};

export function sessionAsAuthHeaderSource(session: PersistentUserSession): AuthHeaderSource {
  return {
    cachedAuthHeaders() {
      return null;
    },
    async newAuthHeaders() {
      const jwt = await session.getFreshJwt();
      return [[MIMIR_JWT_HTTP_HEADER, jwt]];
    }
  };
}
